import React, { useRef, useState } from 'react';
import { Upload } from 'lucide-react';

export default function ResumeUpload({ onFileSelect, maxSizeMB = 5 }) {
    const fileInputRef = useRef(null);
    const [fileName, setFileName] = useState('');
    const [isDragging, setIsDragging] = useState(false);

    const pick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const guard = file => {
        if (file.type !== 'application/pdf') {
            alert('Please upload a PDF file.');
            return false;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
            alert(`File is larger than ${maxSizeMB}MB.`);
            return false;
        }
        return true;
    };

    const processFile = file => {
        if (!guard(file)) return;
        setFileName(file.name);
        onFileSelect(file);
    };

    const onChange = e => {
        const f = e.target.files && e.target.files[0];
        if (f) processFile(f);
    };

    const onDrop = e => {
        e.preventDefault();
        setIsDragging(false);
        const f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) processFile(f);
    };

    const onDragOver = e => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDragLeave = () => {
        setIsDragging(false);
    };

    return (
        <div
            className="mb-6 animate-fade-in-up"
            style={{ animationDelay: '0.2s', opacity: 0 }}
        >
            <p className="text-center text-[var(--charcoal-black)] font-semibold mb-1.5 text-sm">
                Upload your resume for reference
            </p>
            <p className="text-center text-[var(--forest-green)] text-xs mb-4">
                Supported formats: PDF (max {maxSizeMB}MB) - View side-by-side while filling the form
            </p>
            <div
                onClick={pick}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                className={`relative border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all duration-300 ${
                    isDragging
                        ? 'border-[var(--jade-green)] bg-gradient-to-br from-white to-[var(--pale-mint)] shadow-lg scale-[1.02]'
                        : 'border-[var(--forest-green)] bg-white hover:border-[var(--jade-green)] hover:shadow-md hover:bg-gradient-to-br hover:from-white hover:to-[var(--off-white)]'
                }`}
                style={{
                    boxShadow: isDragging 
                        ? '0 8px 24px rgba(7, 98, 76, 0.15)' 
                        : '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={onChange}
                    className="hidden"
                />
                <div className={`transition-transform duration-300 ${isDragging ? 'scale-110' : 'scale-100'}`}>
                    <Upload className="w-8 h-8 text-[var(--forest-green)] mx-auto mb-2" />
                </div>
                <p className="text-[var(--charcoal-black)] font-semibold text-sm mb-0.5">
                    Click to upload your resume
                </p>
                <p className="text-[var(--forest-green)] text-xs opacity-80">or drag and drop</p>
                {fileName && (
                    <div className="mt-3 pt-3 border-t border-[var(--jade-green)]/20">
                        <p className="text-[var(--charcoal-black)] text-xs">
                            📄 <span className="font-semibold">{fileName}</span>
                        </p>
                    </div>
                )}
            </div>
            <div className="text-center mt-3.5">
                <div className="inline-block relative">
                    <div className="absolute inset-0 bg-[var(--jade-green)] opacity-10 rounded-full blur-sm" />
                    <p className="relative px-4 py-1.5 text-[var(--forest-green)] text-xs font-semibold">
                        Fill out the form manually below
                    </p>
                </div>
            </div>
        </div>
    );
}
